import type {DiffEntry} from '@github-ui/diff-lines/types'
import {
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import type {QueryKey} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {produce} from 'immer'
import {getQueryClient} from '@github-ui/react-core/query-client'
import type {ComparisonInfo} from '../../stores/slices/comparison-info'
import {isFeatureEnabled} from '@github-ui/feature-flags'

interface QueryKeyFields {
  basePath: string
  effectiveRange: {
    baseOid: string
    headOid: string
  }
  hideWhitespace: boolean
}

type LoadDiffEntriesArgs = ComparisonInfo & {
  paths: string[]
  contextRanges?: {[path: string]: Array<[number, number]>}
  signal?: AbortSignal
  basePath: string
}

export function diffEntriesKey({basePath, effectiveRange, hideWhitespace}: QueryKeyFields): QueryKey {
  return [
    PageData.diffEntries,
    basePath,
    `baseOid:${effectiveRange.baseOid}`,
    `headOid:${effectiveRange.headOid}`,
    `hideWhitespace:${hideWhitespace}`,
  ]
}

export const loadDiffEntriesForPaths = (args: LoadDiffEntriesArgs) => {
  const newDiffFetchEnabled = isFeatureEnabled('pr_sfv_new_diff_fetch')
  return newDiffFetchEnabled ? loadDiffEntriesForPaths_ff_sfv_new_diff_fetch(args) : loadDiffEntriesForPaths_prev(args)
}

export async function loadDiffEntriesForPaths_prev(variables: LoadDiffEntriesArgs) {
  const queryClient = getQueryClient()
  const queryKey = diffEntriesKey(variables)
  const newDiffEntries = await fetchDiffEntries(variables)

  if (newDiffEntries && newDiffEntries.length > 0) {
    queryClient.setQueryData(
      queryKey,
      produce((oldDiffEntriesData: DiffEntry[]) => {
        if (!oldDiffEntriesData) return newDiffEntries

        // Merge old and new without dupes
        for (const newEntry of newDiffEntries) {
          if (oldDiffEntriesData.find(oldEntry => oldEntry.pathDigest === newEntry.pathDigest)) {
            continue
          }
          oldDiffEntriesData.push(newEntry)
        }
      }),
    )
  }
  return newDiffEntries
}

/**
 * Loads diff entries for the specified paths
 *
 * @returns diff entries for all requested paths, if necessary combining cached and newly fetched entries
 */
export async function loadDiffEntriesForPaths_ff_sfv_new_diff_fetch(variables: LoadDiffEntriesArgs) {
  const queryClient = getQueryClient()
  const queryKey = diffEntriesKey(variables)

  // Check if current query key is cached before requesting it
  // This prevents duplicate requests when returning to the same diff (especially in single file mode)
  // Retrieve existing paths from the TS cache
  const existingDiffEntries = queryClient.getQueryData<DiffEntry[]>(queryKey)

  // Determine which paths still need to be fetched
  let pathsToLoad: string[]
  if (existingDiffEntries?.length) {
    const existingPaths = new Set(existingDiffEntries.map(e => e.path))
    pathsToLoad = variables.paths.filter(p => !existingPaths.has(p))

    // If all requested paths are already cached, return them, and skip the fetch
    if (pathsToLoad.length === 0) {
      return existingDiffEntries
    }
  } else {
    pathsToLoad = variables.paths
  }

  // Only fetch the missing paths
  const newDiffEntries = await fetchDiffEntries({...variables, paths: pathsToLoad})

  // If we have new entries, merge them into the cache
  if (newDiffEntries && newDiffEntries.length > 0) {
    queryClient.setQueryData(
      queryKey,
      produce((oldDiffEntriesData: DiffEntry[]) => {
        if (!oldDiffEntriesData) return newDiffEntries

        // Merge old and new without dupes
        for (const newEntry of newDiffEntries) {
          if (oldDiffEntriesData.find(oldEntry => oldEntry.pathDigest === newEntry.pathDigest)) {
            continue
          }
          oldDiffEntriesData.push(newEntry)
        }
      }),
    )
  }

  // Return all requested diff entries (both cached and newly fetched)
  const allDiffEntries = queryClient.getQueryData<DiffEntry[]>(queryKey) || []
  return allDiffEntries.filter(entry => variables.paths.includes(entry.path))
}

/**
 * Fetches diff entries for given paths
 */
async function fetchDiffEntries({
  basePath,
  fullDiff,
  effectiveRange,
  paths,
  contextRanges,
  signal,
  hideWhitespace,
}: ComparisonInfo & {
  paths: string[]
  contextRanges?: {[path: string]: Array<[number, number]>}
  signal?: AbortSignal
  basePath: string
}): Promise<DiffEntry[] | undefined> {
  const params = new URLSearchParams()
  const encodedPaths = paths.map(path => encodeURIComponent(path))
  params.append('paths', encodedPaths.join(','))

  if (contextRanges) {
    // `ctx` parameter is in the same order as the `paths` parameter.
    // Context ranges are comma-separated, and ranges for paths are
    // colon-separated.
    //
    // For example:
    //     ?paths=a,b&ctx=:1-3,4-7
    //
    // Means that path `a` needs no injected context ranges, and path `b` needs
    // lines 1-3, and 4-7 inclusive.

    const ctx = paths
      .map(path => {
        return (contextRanges[path] ?? []).map(([a, b]) => `${a}-${b}`).join(',')
      })
      .join(':')
    params.append('ctx', ctx)
  }

  params.append('w', hideWhitespace ? '1' : '0')

  if (effectiveRange.baseOid === fullDiff.baseOid) {
    params.append('range', effectiveRange.headOid)
  } else {
    params.append('range', `${effectiveRange.baseOid}..${effectiveRange.headOid}`)
  }

  const fetchUrl = `${basePath}/page_data/${PageData.diffEntries}?${params.toString()}`

  try {
    const response = await reactFetchJSON(fetchUrl, {signal})
    if (signal?.aborted) return undefined
    const data = await parseJSONWithBetterErrors(response)
    throwErrorsIfBadResponse(response, data)
    return data ?? undefined
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return
    } else {
      throw error
    }
  }
}
